import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { ProfileFormComponent } from '../../components/profile-form/profile-form.component';
import { ProfileSettingsService, ProfileSettings } from '../../services/profile-settings.service';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../components/shared/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [CommonModule, TranslocoModule, ProfileFormComponent, FormsModule, BreadcrumbComponent],
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss'],
})
export class ProfilePageComponent implements OnInit {
  userStatuses: string[] = [];
  selectedStatus: string = '';
  isLoadingStatuses = false;
  isUpdatingStatus = false;
  profileSettings: ProfileSettings | null = null;

  readonly breadcrumbItems: BreadcrumbItem[] = [{ labelKey: 'breadcrumbs.home', link: '/' }, { labelKey: 'breadcrumbs.profile' }];

  constructor(
    private profileSettingsService: ProfileSettingsService,
    private authService: AuthService,
    private toastService: ToastService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadUserStatus();
    this.loadProfileSettings();
  }

  /**
   * Load the current user's status from localStorage
   */
  loadUserStatus(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.status) {
      this.selectedStatus = currentUser.status;
    }
  }

  /**
   * Load profile settings (includes user statuses, categories, employment types)
   * This data will be passed down to child components to avoid duplicate API calls
   */
  loadProfileSettings(): void {
    this.isLoadingStatuses = true;
    this.profileSettingsService.getProfileSettings().subscribe({
      next: (settings) => {
        this.profileSettings = settings;
        this.userStatuses = settings.userStatuses || [];
        this.isLoadingStatuses = false;
      },
      error: (error) => {
        console.error('Error loading profile settings:', error);
        this.isLoadingStatuses = false;
        // Fallback to default statuses
        this.userStatuses = ['Available', 'Unavailable', 'Hired'];
      },
    });
  }

  /**
   * Handle status change
   */
  onStatusChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const newStatus = selectElement.value;

    if (!newStatus) {
      return;
    }

    // Get the previous status for comparison
    const currentUser = this.authService.getCurrentUser();
    const previousStatus = currentUser?.status;

    // Don't make API call if status hasn't actually changed
    if (newStatus === previousStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.profileSettingsService.updateUserStatus(newStatus).subscribe({
      next: (response) => {
        this.selectedStatus = newStatus;

        // Update user in localStorage - get fresh reference
        const updatedUser = this.authService.getCurrentUser();
        if (updatedUser) {
          updatedUser.status = newStatus;
          localStorage.setItem('user', JSON.stringify(updatedUser));
          this.authService.currentUserValue = updatedUser;
        }

        console.log('Status updated successfully in localStorage:', newStatus);

        // Show translated success message (synchronous)
        const successTitle = this.translocoService.translate('pages.profile.status.successTitle');
        const successMessage = this.translocoService.translate('pages.profile.status.successMessage');
        this.toastService.success(successTitle, successMessage);

        this.isUpdatingStatus = false;
      },
      error: (error) => {
        console.error('Error updating status:', error);

        // Show translated error message (synchronous)
        const errorTitle = this.translocoService.translate('pages.profile.status.errorTitle');
        const errorMessage = this.translocoService.translate('pages.profile.status.errorMessage');
        this.toastService.error(errorTitle, errorMessage);

        this.isUpdatingStatus = false;
        // Revert to previous status
        this.selectedStatus = previousStatus || '';
      },
    });
  }
}
